import { ApiConst } from '../constants';

/**
 * Customer FAQ chatbot (PPS-341).
 *
 * Deliberately not built on ApiFactory: that layer calls response.json(), which
 * waits for the whole body. The whole point here is to render the answer while
 * it is still being written, so this reads the body as a stream instead. The
 * credential handling below is copied from ApiFactory.getCredential on purpose
 * — same token, same localStorage key. Staff impersonation needs nothing
 * extra: the portal swaps the customer's own token into `token` while acting
 * as them, so the request is already scoped to that customer.
 */

const BASE_URL = process.env.REACT_APP_CHATBOT_API_BASE_URL;

function credentialHeaders() {
    const headers = { 'Content-Type': 'application/json' };

    const token = localStorage.getItem('token');
    if (token) {
        headers.Authorization = `${ApiConst.TOKEN_PREFIX} ${token}`;
    }

    return headers;
}

/**
 * Sends a question and invokes onFrame for each NDJSON frame as it arrives.
 *
 * Frames: { type: 'status', stage } as each step really starts or ends, then
 * { type: 'text', text } repeatedly, then { type: 'sources', sources }, then
 * { type: 'done', sessionId, answerId }. An { type: 'error', message } frame can arrive
 * at any point — including after text has already been rendered, since the
 * status code is long gone by then.
 *
 * history is the conversation so far, oldest first, as { role, text } with
 * role 'user' or 'assistant'. The API keeps nothing between requests, so this
 * is what lets a follow-up such as 「じゃあSは？」 be understood.
 */
export async function askChatbot({ question, history = [], sessionId, signal, onFrame }) {
    const response = await fetch(`${BASE_URL}/api/chat`, {
        method: 'POST',
        headers: credentialHeaders(),
        body: JSON.stringify({ question, history, ...(sessionId ? { sessionId } : {}) }),
        signal
    });

    if (!response.ok || !response.body) {
        // The status travels with the error so the widget can tell a
        // throttled request (429, worth retrying) from a broken one.
        const error = new Error(`chat request failed: ${response.status}`);
        error.status = response.status;
        throw error;
    }

    const reader = response.body.getReader();
    // { stream: true } is not optional. A Japanese character is three UTF-8
    // bytes and a chunk boundary can land inside one; decoding each chunk
    // independently yields a replacement character at every seam.
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    // Every answer ends with a 'done' or an 'error' frame. A stream that just
    // stops -- a Lambda timeout, a proxy closing early -- is a failure even
    // though nothing threw, and has to surface as one.
    let ended = false;
    const deliver = (line) => {
        let frame;
        try {
            frame = JSON.parse(line);
        } catch {
            // A malformed line is not worth failing the whole answer over.
            return;
        }
        if (frame.type === 'done' || frame.type === 'error') ended = true;
        onFrame(frame);
    };

    for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Frames are newline-delimited, and a chunk can end mid-line, so the
        // last piece is kept until its newline arrives.
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
            if (line.trim()) deliver(line);
        }
    }

    buffer += decoder.decode();
    if (buffer.trim()) deliver(buffer);

    if (!ended) {
        throw new Error('chat stream ended before the answer finished');
    }
}

/**
 * The customer's rating of one answer (PPS-390). answerId comes from that
 * answer's 'done' frame. Resolves to whether it was saved; never throws -- a
 * lost rating is not worth showing the customer an error for.
 */
export async function sendFeedback({ sessionId, answerId, rating, reason }) {
    try {
        const response = await fetch(`${BASE_URL}/api/feedback`, {
            method: 'POST',
            headers: credentialHeaders(),
            body: JSON.stringify({ sessionId, answerId, rating, ...(reason ? { reason } : {}) }),
            // Still delivered if the customer logs out or leaves right after.
            keepalive: true
        });
        return response.ok;
    } catch {
        return false;
    }
}
